/**
 * Hook principal del formulario de clientes usando TanStack Form
 * Maneja validación por pasos y estado del wizard
 */
import { useForm } from '@tanstack/react-form'
import { useCallback, useState } from 'react'
import type { z } from 'zod'
import { usePermissions } from '@/hooks/usePermissions'
import {
  clienteSchema,
  STEP_SCHEMAS,
  type ClienteFormValues,
} from './clienteSchema'

export interface UseClienteFormOptions {
  defaultValues?: Partial<ClienteFormValues>
  onSubmit?: (values: ClienteFormValues) => void | Promise<void>
  /** Los pasos a validar, en orden. Por defecto los 4 de siempre; el alta agrega el de deuda inicial. */
  stepSchemas?: readonly z.ZodType<unknown>[]
}

const DEFAULT_VALUES: ClienteFormValues = {
  // Identidad
  tipo_persona: 'fisica',
  nombre: '',
  apellido: '',
  nombre_fantasia: null,
  cuit: '',
  email: '',
  telefono: '',
  admin_nombre: null,
  admin_cuit: null,
  // Domicilio
  domicilio_provincia: '',
  domicilio_localidad: null,
  domicilio_cp: null,
  domicilio_calle: null,
  domicilio_numero: null,
  domicilio_piso: null,
  domicilio_depto: null,
  domicilio_barrio: null,
  domicilio_referencia: null,
  // Fiscal
  condicion_iva: null,
  categoria_monotributo: null,
  provincia: null,
  actividad: null,
  codigo_actividad_afip: null,
  actividades_economicas: [],
  regimen_iibb: null,
  cliente_desde: null,
  cierre_ejercicio: null,
  impuestos_liquidar: [],
  // Datos de Inscripción (solo juridica)
  inscripcion_numero: null,
  inscripcion_fojas: null,
  inscripcion_tomo: null,
  inscripcion_protocolo: null,
  inscripcion_fecha: null,
  inscripcion_ano: null,
  // Servicios
  responsables: [],
  descripcion_tarea: null,
  honorarios_mensuales: 0,
  estado: 'Activo',
  serviciosConPrecio: [],
  etiquetas: [],
  deudasIniciales: [],
}

export function useClienteForm(options: UseClienteFormOptions = {}) {
  const { defaultValues, onSubmit, stepSchemas = STEP_SCHEMAS } = options
  const [currentStep, setCurrentStep] = useState(0)
  const [stepErrors, setStepErrors] = useState<Record<number, string[]>>({})
  const { puede } = usePermissions()

  // Crear instancia del formulario con TanStack Form.
  // Sin permiso para asignar responsables, el campo arranca en undefined: no se muestra ni se valida.
  const form = useForm({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
      ...(puede.asignarResponsablesCliente ? {} : { responsables: undefined }),
    } as ClienteFormValues,
    validators: {
      // Validación completa al submit
      onSubmit: clienteSchema,
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value as ClienteFormValues)
      }
    },
  })

  /**
   * Valida un paso específico del wizard
   * @returns true si el paso es válido, false si hay errores
   */
  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      const schema = stepSchemas[stepIndex]
      if (!schema) return true

      const values = form.state.values

      try {
        // Parsear con el schema del paso
        schema.parse(values)

        // Validaciones condicionales adicionales que dependen de campos de otros pasos
        // Paso 2 (Fiscal): cierre_ejercicio requerido solo para sociedades
        if (stepIndex === 2 && values.tipo_persona === 'juridica' && !values.cierre_ejercicio) {
          // Marcar el campo con error para que se muestre en la UI
          form.setFieldMeta('cierre_ejercicio', (prev) => ({
            ...prev,
            errors: ['El cierre de ejercicio es requerido para sociedades'],
          }))
          setStepErrors((prev) => ({
            ...prev,
            [stepIndex]: ['El cierre de ejercicio es requerido para sociedades'],
          }))
          return false
        }

        // Limpiar errores del paso
        setStepErrors((prev) => {
          const next = { ...prev }
          delete next[stepIndex]
          return next
        })
        return true
      } catch (error) {
        // Zod error - puede tener 'errors' (v3) o 'issues' (v4)
        type ZodIssue = { path: (string | number)[]; message: string }
        const zodError = error as { errors?: ZodIssue[]; issues?: ZodIssue[] }
        const errorList = zodError.errors || zodError.issues || []

        // Marcar cada campo con su error específico para mostrarlo en la UI
        errorList.forEach((issue) => {
          const fieldName = issue.path[0] as keyof ClienteFormValues
          if (fieldName) {
            form.setFieldMeta(fieldName, (prev) => ({
              ...prev,
              errors: [issue.message],
            }))
          }
        })

        const messages = errorList.map((e) => e.message)
        setStepErrors((prev) => ({
          ...prev,
          [stepIndex]: messages,
        }))
        return false
      }
    },
    [form, stepSchemas]
  )

  /**
   * Intenta avanzar al siguiente paso
   * Valida el paso actual primero
   */
  const nextStep = useCallback(async (): Promise<boolean> => {
    const isValid = await validateStep(currentStep)
    if (isValid && currentStep < stepSchemas.length - 1) {
      setCurrentStep((prev) => prev + 1)
      return true
    }
    return false
  }, [currentStep, validateStep, stepSchemas])

  /**
   * Retrocede al paso anterior
   * Siempre permitido
   */
  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }, [currentStep])

  /**
   * Ir a un paso específico (para modo edición)
   * En modo edición se permite saltar, en creación solo a pasos anteriores
   */
  const goToStep = useCallback(
    async (targetStep: number, isEditMode = false): Promise<boolean> => {
      if (targetStep < 0 || targetStep >= stepSchemas.length) return false

      // Si va hacia atrás, siempre permitido
      if (targetStep < currentStep) {
        setCurrentStep(targetStep)
        return true
      }

      // Si es modo edición, permitir saltar
      if (isEditMode) {
        setCurrentStep(targetStep)
        return true
      }

      // En creación, validar cada paso intermedio
      for (let step = currentStep; step < targetStep; step++) {
        const isValid = await validateStep(step)
        if (!isValid) {
          setCurrentStep(step)
          return false
        }
      }

      setCurrentStep(targetStep)
      return true
    },
    [currentStep, validateStep, stepSchemas]
  )

  /**
   * Verifica si un paso está completo (sin errores)
   */
  const isStepComplete = useCallback(
    (stepIndex: number): boolean => {
      return !stepErrors[stepIndex] || stepErrors[stepIndex].length === 0
    },
    [stepErrors]
  )

  /**
   * Reset del formulario
   */
  const reset = useCallback(() => {
    form.reset()
    setCurrentStep(0)
    setStepErrors({})
  }, [form])

  return {
    form,
    currentStep,
    stepErrors,
    totalSteps: stepSchemas.length,
    // Navegación
    nextStep,
    prevStep,
    goToStep,
    setCurrentStep,
    // Validación
    validateStep,
    isStepComplete,
    // Utilidades
    reset,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === stepSchemas.length - 1,
  }
}

export type UseClienteFormReturn = ReturnType<typeof useClienteForm>

/**
 * Tipo simplificado del formulario para usar en componentes de steps
 * Evita tener que especificar los 12 argumentos genéricos de ReactFormExtendedApi
 */
export type ClienteForm = UseClienteFormReturn['form']

/**
 * Configuración simplificada de paso para el Stepper
 * Solo necesita lo que muestra visualmente, no campos de validación
 */
export interface WizardStepConfig {
  id: string
  title: string
  description: string
}

/**
 * Helper para extraer mensaje de error de TanStack Form
 * En TanStack Form v1 con Zod 4, los errores pueden ser objetos StandardSchemaV1Issue
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (typeof first === 'object' && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}
